import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useFidelityNumberMutation } from "@/query/shopDataQuery"
import { StarIcon } from "@radix-ui/react-icons"
import React, { useState } from "react"

type EditFidelityModalProps = {
  isOpen: boolean
  currentFidelityNumber: number
  onClose: () => void
}

export const EditFidelityModal: React.FC<EditFidelityModalProps> = ({
  isOpen,
  currentFidelityNumber,
  onClose,
}) => {
  const [fidelityNumber, setFidelityNumber] = useState<number>(
    currentFidelityNumber
  )
  const [error, setError] = useState<string>("")
  const { fidelityNumberMutation } = useFidelityNumberMutation()

  const handleSave = () => {
    // Validation
    if (fidelityNumber < 1) {
      setError("Le nombre de points doit être supérieur à 0.")
      return
    }
    if (fidelityNumber > 10000) {
      setError("Le nombre de points ne peut pas dépasser 10 000.")
      return
    }

    setError("")
    fidelityNumberMutation({ fidelityNumber })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0
    setFidelityNumber(value)
    if (error) setError("") // Effacer l'erreur quand l'utilisateur tape
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <StarIcon className="h-5 w-5 mr-2 text-yellow-500" />
            Modifier le programme de fidélité
          </DialogTitle>
          <DialogDescription>
            Définissez le nombre de points nécessaires pour obtenir une
            récompense.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Affichage de la valeur actuelle */}
          <div className="space-y-3">
            <Label>Configuration actuelle</Label>
            <div className="flex items-center space-x-3 p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800/50">
              <StarIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {currentFidelityNumber} points requis
              </span>
            </div>
          </div>

          {/* Contrôle pour modifier la valeur */}
          <div className="space-y-3">
            <Label htmlFor="fidelityInput">Nouveau nombre de points</Label>
            <Input
              id="fidelityInput"
              type="number"
              min="1"
              max="10000"
              value={fidelityNumber}
              onChange={handleInputChange}
              placeholder="Ex: 100"
              className={error ? "border-red-500" : ""}
            />
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Les clients devront accumuler ce nombre de points pour obtenir une
              récompense.
            </p>
          </div>

          {/* Aperçu */}
          <div className="space-y-3">
            <Label>Aperçu</Label>
            <div className="flex items-center space-x-3 p-3 border border-slate-200 dark:border-slate-700 rounded-md bg-blue-50 dark:bg-blue-900/20">
              <StarIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
              <span className="font-medium text-slate-700 dark:text-slate-200">
                {fidelityNumber} points requis pour une récompense
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={fidelityNumber < 1 || fidelityNumber > 10000}
          >
            Sauvegarder les modifications
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
